// src/components/institution/forms/GalleryForm.jsx
import React, { useState, useEffect } from 'react';
import { X, Save, Upload, Image, Grid, Eye, Loader2, Plus, Trash2, Move } from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { firestoreService } from '../../../services/firestoreService';

export const GalleryForm = ({ gallery, onSave, onClose, bookId }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'campus',
    coverImage: '',
    images: [],
    isPublic: true,
    tags: [],
    sortOrder: 0
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [errors, setErrors] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (gallery) {
      setFormData(gallery);
    }
  }, [gallery]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle bulk image upload using FileUpload component
  const handleBulkImageUpload = async (selectedFiles, error) => {
    if (error) {
      alert(`Upload Error: ${error}`);
      return;
    }

    if (!selectedFiles || selectedFiles.length === 0) return;

    const files = Array.isArray(selectedFiles) ? selectedFiles : [selectedFiles];
    
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    try {
      // Use firestoreService uploadMultipleFiles with proper path
      const results = await firestoreService.uploadMultipleFiles(
        files,
        (file) => firestoreService.uploadFile(file, `pb-${bookId}/galleries/images`),
        (current, total, result, uploadError) => {
          setUploadProgress({ current, total });
          if (uploadError) {
            console.error('Upload error:', uploadError);
          }
        }
      );

      // Process successful uploads
      const successfulUploads = results
        .filter(result => result.success)
        .map(result => ({
          id: Date.now() + Math.random(),
          url: result.url,
          caption: '',
          fileName: result.originalFileName,
          fileType: result.fileType,
          uploadedAt: result.uploadedAt
        }));

      if (successfulUploads.length > 0) {
        setFormData(prev => ({
          ...prev,
          images: [...(prev.images || []), ...successfulUploads]
        }));
      }

      // Show results
      const failedCount = results.filter(r => !r.success).length;
      if (failedCount > 0) {
        alert(`${successfulUploads.length} files uploaded successfully. ${failedCount} failed.`);
      }

    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images: ' + error.message);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  // Handle cover image upload using FileUpload component
  const handleCoverImageUpload = async (selectedFile, error) => {
    if (error) {
      alert(`Cover Image Error: ${error}`);
      return;
    }

    if (!selectedFile) return;

    setUploading(true);
    try {
      const result = await firestoreService.uploadFile(
        selectedFile, 
        `pb-${bookId}/galleries/covers`
      );
      
      setFormData(prev => ({ ...prev, coverImage: result.url }));
    } catch (error) {
      console.error('Error uploading cover image:', error);
      alert('Failed to upload cover image: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Remove individual image
  const removeImage = (index) => {
    const updatedImages = formData.images.filter((_, i) => i !== index);
    handleInputChange('images', updatedImages);
  };

  // Update image caption
  const updateImageCaption = (index, caption) => {
    const updatedImages = [...formData.images];
    updatedImages[index] = { ...updatedImages[index], caption };
    handleInputChange('images', updatedImages);
  };

  // Drag and drop functionality for reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const images = [...formData.images];
    const draggedItem = images[draggedIndex];
    images.splice(draggedIndex, 1);
    images.splice(dropIndex, 0, draggedItem);

    handleInputChange('images', images);
    setDraggedIndex(null);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Gallery title is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await onSave({
        ...formData,
        imageCount: formData.images?.length || 0,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving gallery:', error);
      alert('Failed to save gallery: ' + error.message);
    }
  };

  const categories = [
    { value: 'campus', label: 'Campus Life' },
    { value: 'events', label: 'Events' },
    { value: 'facilities', label: 'Facilities' },
    { value: 'academic', label: 'Academic' },
    { value: 'sports', label: 'Sports' },
    { value: 'arts', label: 'Arts & Culture' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Image className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {gallery ? 'Edit Gallery' : 'Create New Gallery'}
              </h2>
              <p className="text-sm text-gray-600">
                Organize and showcase photos with modern upload tools
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={uploading}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-8">
            
            {/* Basic Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gallery Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter gallery title..."
                  disabled={uploading}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.category ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={uploading}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Describe this gallery..."
                disabled={uploading}
              />
            </div>

            {/* Cover Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Upload className="h-4 w-4 inline mr-1" />
                Cover Image
              </label>
              <div className="space-y-4">
                <FileUpload
                  onFileSelect={handleCoverImageUpload}
                  currentFile={formData.coverImage}
                  onRemove={() => handleInputChange('coverImage', '')}
                  accept="image/*"
                  maxSize={10}
                  label="Choose Cover Image"
                  uploading={uploading}
                  showPreview={true}
                />
              </div>
            </div>

            {/* Gallery Images Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Grid className="h-4 w-4 inline mr-1" />
                Gallery Images
                {uploading && uploadProgress.total > 0 && (
                  <span className="ml-2 text-sm text-indigo-600">
                    Uploading {uploadProgress.current} of {uploadProgress.total}...
                  </span>
                )}
              </label>
              
              {/* Bulk Upload */}
              <div className="mb-6">
                <FileUpload
                  onFileSelect={handleBulkImageUpload}
                  accept="image/*,video/*"
                  maxSize={20}
                  label="Upload Multiple Images"
                  uploading={uploading}
                  multiple={true}
                  showPreview={false}
                />
              </div>

              {/* Upload Progress */}
              {uploading && uploadProgress.total > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading files... ({uploadProgress.current}/{uploadProgress.total})
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${(uploadProgress.current / uploadProgress.total) * 100}%`
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Image Gallery Grid */}
              {formData.images && formData.images.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">
                      Gallery Images ({formData.images.length})
                    </h4>
                    <p className="text-xs text-gray-500">
                      Drag to reorder images
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div
                        key={image.id || index}
                        className="relative group border border-gray-200 rounded-lg p-2 bg-white hover:shadow-md transition-all duration-200 cursor-move"
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                      >
                        {/* Image Preview */}
                        <div className="relative">
                          <img
                            src={image.url}
                            alt={image.caption || `Gallery image ${index + 1}`}
                            className="w-full h-24 object-cover rounded-md"
                          />
                          
                          {/* Drag Handle */}
                          <div className="absolute top-1 left-1 bg-black bg-opacity-50 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Move className="h-3 w-3 text-white" />
                          </div>

                          {/* Remove Button */}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>

                        {/* Caption Input */}
                        <div className="mt-2">
                          <input
                            type="text"
                            value={image.caption || ''}
                            onChange={(e) => updateImageCaption(index, e.target.value)}
                            placeholder="Add caption..."
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={uploading}
                          />
                        </div>

                        {/* File Info */}
                        {image.fileName && (
                          <div className="mt-1 text-xs text-gray-500 truncate">
                            {image.fileName}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Settings */}
            <div className="flex items-center gap-6 pt-4 border-t border-gray-100">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  disabled={uploading}
                />
                <span className="ml-2 text-sm text-gray-700 flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  Public Gallery
                </span>
              </label>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Sort Order:</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 0)}
                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                  disabled={uploading}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-500">
              {formData.images?.length > 0 && (
                `${formData.images.length} image${formData.images.length !== 1 ? 's' : ''} selected`
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Gallery
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};